import pinApi from '@/api/pinApi'
import PasswordField from '@/components/form-control/PasswordField'
import PinInput from '@/components/form-control/PinInput'
import { queryKeys } from '@/constant/common'
import { useTranslation } from '@/hooks/useTranslation'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import * as yup from 'yup'

interface PinSetupViewProps {
  closeModal: () => void
  hasPin?: boolean
  onSuccess?: () => void
}

export enum PinView {
  CREATE = 'create',
  CHANGE = 'change',
  RESET = 'reset',
}

const PREDICTABLE_PINS = [
  '123456',
  '234567',
  '345678',
  '456789',
  '012345',
  '654321',
  '765432',
  '876543',
  '987654',
  '543210',
  '112233',
  '121212',
  '123123',
  '123321',
  '101010',
]

const isPredictablePin = (pin: string): boolean => {
  // Check for 6 identical digits (000000, 111111, ..., 999999)
  if (/^(\d)\1{5}$/.test(pin)) return true
  return PREDICTABLE_PINS.includes(pin)
}

const pinValidation = yup
  .string()
  .required('PIN is required')
  .matches(/^\d{6}$/, 'PIN must be exactly 6 digits')
  .test(
    'not-predictable',
    'This PIN is too predictable. Please choose a different one.',
    (value) => {
      if (!value) return true
      return !isPredictablePin(value)
    },
  )

// ── Schema per view ─────────────────────────────────────────────────────

const createSchema = yup.object({
  currentPassword: yup
    .string()
    .required('Current password is required')
    .min(5, 'Minimum 5 characters'),
  pin: pinValidation,
  confirmPin: yup
    .string()
    .required('Confirm PIN is required')
    .test('pins-match', 'PINs do not match', function (value) {
      return this.parent.pin === value
    }),
})

const changeSchema = yup.object({
  currentPin: yup
    .string()
    .required('Current PIN is required')
    .matches(/^\d{6}$/, 'PIN must be exactly 6 digits'),
  newPin: pinValidation
    .clone()
    .test(
      'not-same',
      'New PIN must be different from current PIN',
      function (value) {
        return value !== this.parent.currentPin
      },
    ),
  confirmNewPin: yup
    .string()
    .required('Confirm new PIN is required')
    .test('pins-match', 'PINs do not match', function (value) {
      return this.parent.newPin === value
    }),
})

const resetSchema = yup.object({
  code: yup
    .string()
    .required('Reset code is required')
    .matches(/^\d{6}$/, 'Code must be exactly 6 digits'),
  pin: pinValidation,
  confirmPin: yup
    .string()
    .required('Confirm PIN is required')
    .test('pins-match', 'PINs do not match', function (value) {
      return this.parent.pin === value
    }),
})

// ── Helper Sub-Components ───────────────────────────────────────────────

interface FormFieldProps {
  label: string
  children: React.ReactNode
}

const FormField = ({ label, children }: FormFieldProps) => (
  <Box>
    <Typography sx={{ fontSize: '12px', color: 'text.main', mb: '4px' }}>
      {label}
    </Typography>
    {children}
  </Box>
)

interface FormActionsProps {
  leftAction?: React.ReactNode
  loading: boolean
  onCancel: () => void
}

const FormActions = ({ leftAction, loading, onCancel }: FormActionsProps) => (
  <Stack
    direction="row"
    sx={{ justifyContent: 'space-between', alignItems: 'center', mt: '10px' }}
  >
    {leftAction || <Box />}
    <Stack direction="row" spacing="10px">
      <Button
        variant="secondary"
        sx={{ width: '99px', maxHeight: '26px' }}
        onClick={onCancel}
      >
        Cancel
      </Button>
      <LoadingButton
        variant="primary"
        type="submit"
        size="small"
        loading={loading}
        sx={{
          fontSize: '12px',
          fontWeight: 400,
          width: '88px',
          maxHeight: '26px',
          borderRadius: '4px',
          '&:disabled': { backgroundColor: 'grey.600' },
        }}
      >
        Submit
      </LoadingButton>
    </Stack>
  </Stack>
)

// ── Component ───────────────────────────────────────────────────────────

const PinSetupView = ({
  closeModal,
  hasPin = false,
  onSuccess,
}: PinSetupViewProps) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const inputRefPassword = useRef<HTMLInputElement>(null)
  const [loadingButton, setLoadingButton] = useState(false)
  const [currentView, setCurrentView] = useState<PinView>(
    hasPin ? PinView.CHANGE : PinView.CREATE,
  )
  const [resetCodeSent, setResetCodeSent] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  // ── Forms ───────────────────────────────────────────────────────────

  const createForm = useForm({
    defaultValues: { currentPassword: '', pin: '', confirmPin: '' },
    resolver: yupResolver(createSchema),
  })

  const changeForm = useForm({
    defaultValues: { currentPin: '', newPin: '', confirmNewPin: '' },
    resolver: yupResolver(changeSchema),
  })

  const resetForm = useForm({
    defaultValues: { code: '', pin: '', confirmPin: '' },
    resolver: yupResolver(resetSchema),
  })

  useEffect(() => {
    if (currentView === PinView.CREATE && inputRefPassword.current) {
      inputRefPassword.current.focus()
    }
  }, [currentView])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [resendCooldown])

  // ── Handlers ────────────────────────────────────────────────────────

  const completePinUpdate = (message: string): void => {
    toast.success(message)
    if (onSuccess) {
      onSuccess()
      return
    }
    closeModal()
  }

  const handleCreatePin = async (data: {
    currentPassword: string
    pin: string
    confirmPin: string
  }) => {
    try {
      setLoadingButton(true)
      await pinApi.createPin(data)
      await queryClient.invalidateQueries([queryKeys.user])
      completePinUpdate(t('pinCreatedSuccess'))
    } catch (error: any) {
      // Clear PIN inputs on submit failure
      createForm.setValue('pin', '')
      createForm.setValue('confirmPin', '')

      const status = error?.response?.status
      if (status === 409) {
        // PIN already exists — switch to change view
        toast.error(t('pinAlreadyExists'))
        setCurrentView(PinView.CHANGE)
      } else if (status === 400) {
        createForm.setError('currentPassword', {
          message: error?.response?.data?.Message || 'Invalid password',
        })
      } else if (status === 403) {
        toast.error(error?.response?.data?.Message || 'Access denied')
      } else {
        toast.error(error?.message || 'Something went wrong')
      }
    } finally {
      setLoadingButton(false)
    }
  }

  const handleCreateInvalid = () => {
    createForm.setValue('pin', '')
    createForm.setValue('confirmPin', '')
  }

  const handleChangePin = async (data: {
    currentPin: string
    newPin: string
    confirmNewPin: string
  }) => {
    try {
      setLoadingButton(true)
      await pinApi.changePin(data)
      await queryClient.invalidateQueries([queryKeys.user])
      completePinUpdate(t('pinChangedSuccess'))
    } catch (error: any) {
      // Clear PIN inputs on submit failure
      changeForm.setValue('currentPin', '')
      changeForm.setValue('newPin', '')
      changeForm.setValue('confirmNewPin', '')

      const status = error?.response?.status
      if (status === 400) {
        changeForm.setError('currentPin', {
          message: error?.response?.data?.Message || 'Invalid PIN',
        })
      } else if (status === 403) {
        toast.error(error?.response?.data?.Message || 'Access denied')
      } else {
        toast.error(error?.message || 'Something went wrong')
      }
    } finally {
      setLoadingButton(false)
    }
  }

  const handleChangeInvalid = () => {
    changeForm.setValue('currentPin', '')
    changeForm.setValue('newPin', '')
    changeForm.setValue('confirmNewPin', '')
  }

  const handleRequestResetCode = async () => {
    try {
      setLoadingButton(true)
      await pinApi.requestPinReset()
      setResetCodeSent(true)
      setCurrentView(PinView.RESET)
      setResendCooldown(60)
      toast.success(t('pinResetCodeSent'))
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 409) {
        toast.error('Please wait before requesting a new code')
        setCurrentView(PinView.RESET)
        setResetCodeSent(true)
      } else {
        toast.error(error?.message || 'Something went wrong')
      }
    } finally {
      setLoadingButton(false)
    }
  }

  const handleConfirmReset = async (data: {
    code: string
    pin: string
    confirmPin: string
  }) => {
    try {
      setLoadingButton(true)
      await pinApi.confirmPinReset(data)
      await queryClient.invalidateQueries([queryKeys.user])
      completePinUpdate(t('pinResetSuccess'))
    } catch (error: any) {
      // Clear inputs on submit failure
      resetForm.setValue('code', '')
      resetForm.setValue('pin', '')
      resetForm.setValue('confirmPin', '')

      const status = error?.response?.status
      if (status === 400) {
        resetForm.setError('code', {
          message: error?.response?.data?.Message || 'Invalid code or PIN',
        })
      } else {
        toast.error(error?.message || 'Something went wrong')
      }
    } finally {
      setLoadingButton(false)
    }
  }

  const handleResetInvalid = () => {
    resetForm.setValue('code', '')
    resetForm.setValue('pin', '')
    resetForm.setValue('confirmPin', '')
  }

  // ── View titles ─────────────────────────────────────────────────────

  const viewTitle: Record<PinView, string> = {
    [PinView.CREATE]: t('createPin'),
    [PinView.CHANGE]: t('changePin'),
    [PinView.RESET]: t('resetPin'),
  }

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <Stack
      sx={{
        backgroundColor: 'background.default',
        width: '400px',
        minHeight: '281px',
        padding: '30px',
        boxShadow: '0px 0px 30px rgba(132, 132, 132, 0.25)',
      }}
    >
      <Typography
        sx={{
          color: 'text.main',
          marginBottom: '20px',
          fontSize: '16px',
        }}
        variant="h3"
      >
        {viewTitle[currentView]}
      </Typography>

      {/* ── Create PIN ─────────────────────────────────────────────── */}
      {currentView === PinView.CREATE && (
        <form
          onSubmit={createForm.handleSubmit(
            handleCreatePin,
            handleCreateInvalid,
          )}
        >
          <Stack spacing="15px">
            <FormField label={t('currentPassword')}>
              <PasswordField
                name="currentPassword"
                form={createForm}
                placeholder={t('currentPassword')}
                showErrorMessage={true}
                inputRef={inputRefPassword}
              />
            </FormField>
            <FormField label={t('pinCode')}>
              <PinInput name="pin" form={createForm} />
            </FormField>
            <FormField label={t('confirmPin')}>
              <PinInput name="confirmPin" form={createForm} />
            </FormField>
            <FormActions
              loading={loadingButton}
              onCancel={closeModal}
              leftAction={
                <Button
                  variant="text"
                  type="button"
                  onClick={() => setCurrentView(PinView.CHANGE)}
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontSize: '12px',
                    color: 'primary.main',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {t('alreadyHavePin')}
                </Button>
              }
            />
          </Stack>
        </form>
      )}

      {/* ── Change PIN ─────────────────────────────────────────────── */}
      {currentView === PinView.CHANGE && (
        <form
          onSubmit={changeForm.handleSubmit(
            handleChangePin,
            handleChangeInvalid,
          )}
        >
          <Stack spacing="15px">
            <FormField label={t('currentPin')}>
              <PinInput name="currentPin" form={changeForm} />
            </FormField>
            <FormField label={t('newPin')}>
              <PinInput name="newPin" form={changeForm} />
            </FormField>
            <FormField label={t('confirmNewPin')}>
              <PinInput name="confirmNewPin" form={changeForm} />
            </FormField>
            <FormActions
              loading={loadingButton}
              onCancel={closeModal}
              leftAction={
                <Button
                  variant="text"
                  type="button"
                  onClick={handleRequestResetCode}
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontSize: '12px',
                    color: 'primary.main',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {t('forgotPin')}
                </Button>
              }
            />
          </Stack>
        </form>
      )}

      {/* ── Reset PIN ──────────────────────────────────────────────── */}
      {currentView === PinView.RESET && (
        <form
          onSubmit={resetForm.handleSubmit(
            handleConfirmReset,
            handleResetInvalid,
          )}
        >
          <Stack spacing="15px">
            {resetCodeSent && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: 'primary.main',
                  backgroundColor: 'rgba(0, 71, 68, 0.08)',
                  padding: '8px 12px',
                  borderRadius: '4px',
                }}
              >
                {t('pinResetCodeSent')}
              </Typography>
            )}
            <FormField label={t('resetCode')}>
              <PinInput name="code" form={resetForm} />
            </FormField>
            <FormField label={t('newPin')}>
              <PinInput name="pin" form={resetForm} />
            </FormField>
            <FormField label={t('confirmPin')}>
              <PinInput name="confirmPin" form={resetForm} />
            </FormField>
            <FormActions
              loading={loadingButton}
              onCancel={closeModal}
              leftAction={
                <Button
                  variant="text"
                  type="button"
                  onClick={handleRequestResetCode}
                  disabled={resendCooldown > 0}
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontSize: '12px',
                    color: 'primary.main',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                    '&.Mui-disabled': {
                      color: 'grey.600 !important',
                      backgroundColor: 'transparent !important',
                    },
                  }}
                >
                  {resendCooldown > 0
                    ? `Resend code (${resendCooldown}s)`
                    : t('resendCode')}
                </Button>
              }
            />
          </Stack>
        </form>
      )}
    </Stack>
  )
}

export default PinSetupView
